import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import useContract from "../hooks/useContract";

const BurnTokensPage = () => {
  const {
    burnTokens,
    getTokenBalance,
    getGlobalBurnStats,
    getUserBurnHistory,
  } = useContract();

  /* ----------------------------------------
      GLOBAL STATS
  ---------------------------------------- */
  const [globalStats, setGlobalStats] = useState({
    burnedTokens: [],
    totalPerToken: {},
    totalGlobalBurned: 0,
    burnCount: 0,
  });

  const fetchGlobalStats = useCallback(async () => {
    try {
      const stats = await getGlobalBurnStats();
      setGlobalStats(stats);
    } catch (err) {
      console.error("Failed to fetch global stats:", err);
    }
  }, [getGlobalBurnStats]);

  useEffect(() => {
    fetchGlobalStats();
  }, [fetchGlobalStats]);

  /* ----------------------------------------
      USER HISTORY
  ---------------------------------------- */
  const [userHistory, setUserHistory] = useState([]);

  const fetchUserHistory = useCallback(async () => {
    try {
      const history = await getUserBurnHistory();
      setUserHistory(history);
    } catch (err) {
      console.error("Failed to fetch user history:", err);
    }
  }, [getUserBurnHistory]);

  useEffect(() => {
    fetchUserHistory();
  }, [fetchUserHistory]);

  /* ----------------------------------------
      BURN FORM
  ---------------------------------------- */
  const [tokenAddress, setTokenAddress] = useState("");
  const [tokenBalance, setTokenBalance] = useState(0);
  const [amountToBurn, setAmountToBurn] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchBalance = useCallback(async () => {
    if (!tokenAddress || !/^0x[a-fA-F0-9]{40}$/.test(tokenAddress)) {
      setTokenBalance(0);
      return;
    }
    try {
      const bal = await getTokenBalance(tokenAddress);
      setTokenBalance(bal);
    } catch (err) {
      console.error("Failed to fetch balance:", err);
      setTokenBalance(0);
    }
  }, [tokenAddress, getTokenBalance]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  const handleBurn = async () => {
    if (!tokenAddress || !amountToBurn || Number(amountToBurn) <= 0) return;
    setLoading(true);

    try {
      const tx = await burnTokens(tokenAddress, Number(amountToBurn));
      toast.success(`🔥 Burn Successful! TX: ${tx}`);

      setAmountToBurn("");
      fetchBalance();
      fetchGlobalStats();
      fetchUserHistory();
    } catch (err) {
      console.error(err);
      toast.error("❌ Burn failed.");
    }

    setLoading(false);
  };

  const handleMax = () => setAmountToBurn(tokenBalance.toString());
function formatUnits(raw, decimals) {
  raw = Number(raw);
  return raw / Math.pow(10, decimals);
}


  /* ----------------------------------------
      JSX
  ---------------------------------------- */
  return (
    <div className="max-w-7xl mx-auto p-6 text-white space-y-8">
      
      {/* GLOBAL BOX — matches 'Your Totals' style */}
      <div className="bg-black border border-[#ffd16655] rounded-xl p-6">
        <h2 className="text-xl font-bold text-[#ffd166] mb-4">
          🌐 Global Burn Stats
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {/* Burn Count */}
          <div className="p-4 bg-black border border-[#ffd16633] rounded-lg">
            <p className="text-xs text-gray-400">Total Burn Count</p>
            <p className="text-2xl font-bold text-[#ffd166]">{globalStats.burnCount}</p>
          </div>

          {/* Total Global Burn */}
          <div className="p-4 bg-black border border-[#ffd16633] rounded-lg">
            <p className="text-xs text-gray-400">Total Burned Amount</p>
            <p className="text-2xl font-bold text-[#ffd166]">
              {globalStats.totalGlobalBurned}
            </p>
          </div>

          {/* Per Token */}
       <div className="p-4 bg-black border border-[#ffd16633] rounded-lg">
  <p className="text-xs text-gray-400">Per Token Burned</p>

  <ul className="mt-2 space-y-1 text-sm max-h-24 overflow-y-auto">
    {globalStats.burnedTokens.map((token) => {
      const raw = globalStats.totalPerToken[token]; // raw number from backend
      const decimals = globalStats.decimals?.[token] ?? 18;

      const human = formatUnits(raw, decimals);

      return (
        <li key={token} className="text-[#ffd166]">
          <a
            href={`https://bscscan.com/token/${token}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 underline hover:text-blue-300"
          >
            {token.slice(0, 6)}...{token.slice(-4)}
          </a>

          <span className="text-white"> : {human}</span>
        </li>
      );
    })}
  </ul>
</div>

        </div>
      </div>

      {/* BURN FORM + HISTORY */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* BURN FORM */}
        <div className="p-6 bg-black border border-[#ffd16655] rounded-xl space-y-4">
          <h2 className="text-xl font-bold text-[#ffd166]">🔥 Burn Tokens</h2>

          {/* Token Address */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-400">Token Address</label>
            <input
              value={tokenAddress}
              onChange={(e) => setTokenAddress(e.target.value)}
              className="bg-transparent border border-[#ffd16655] rounded px-3 py-2 text-sm"
              placeholder="0x..."
            />
          </div>

          {/* Amount */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-400 flex justify-between">
              Amount to Burn
              <span className="text-[#ffd166] text-[11px]">Balance: {tokenBalance}</span>
            </label>

            <div className="relative">
              <input
                type="number"
                value={amountToBurn}
                onChange={(e) => setAmountToBurn(e.target.value)}
                className="w-full bg-transparent border border-[#ffd16655] rounded px-3 py-2 pr-12 text-sm"
                placeholder="Enter amount"
              />

              <button
                onClick={handleMax}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs font-semibold bg-[#ffd166] text-black rounded"
              >
                Max
              </button>
            </div>
          </div>

          {/* Burn Button */}
          <button
            onClick={handleBurn}
            disabled={loading}
            className="w-full py-2 rounded-full bg-gradient-to-r from-[#ffd166] via-[#ff9f1c] to-[#5c3b00] text-black font-semibold text-sm disabled:opacity-50"
          >
            {loading ? "Burning..." : "Burn Tokens"}
          </button>
        </div>

        {/* USER HISTORY */}
<div className="p-6 bg-black border border-[#ffd16655] rounded-xl space-y-4">
  <h2 className="text-xl font-bold text-[#ffd166]">📜 Your Burn History</h2>

  {userHistory.length === 0 ? (
    <p className="text-gray-400">No burns yet.</p>
  ) : (
    <ul className="space-y-3 text-sm">
      {userHistory.map((item, i) => (
        <li
          key={i}
          className="p-3 border border-[#ffd16622] rounded-lg bg-[#111]"
        >
          {/* TOKEN */}
          <div className="text-[#ffd166] font-semibold">
            Token:&nbsp;
            <a
              href={`https://bscscan.com/token/${item.token}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 underline"
            >
              {item.token.slice(0, 6)}...{item.token.slice(-4)}
            </a>
          </div>

          {/* AMOUNT */}
          <div className="text-gray-300">
            Burned:&nbsp;
            <span className="text-white font-semibold">{item.amount}</span>
          </div>

         

          {/* TIMESTAMP */}
          <div className="text-[11px] text-gray-400 mt-1">
            {new Date(item.timestamp * 1000).toLocaleString()}
          </div>
        </li>
      ))}
    </ul>
  )}
</div>

      </div>
    </div>
  );
};

export default BurnTokensPage;
